import React, { useState, useEffect } from 'react';

import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import { PrimaryBtn } from 'components/UI/Buttons';
import useIsMobile from 'hooks/useIsMobile';

const GradientBackground = styled(Box)(({ theme }) => ({
  backgroundImage: 'linear-gradient(to bottom, #15183A, #638DB8)',
  backgroundBlendMode: 'overlay',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(0),
}));

const TypeWriterTexts = [
  'hablando con tus leads al instante que cotizan.',
  'ahorrándoles tiempo a tus ejecutivos.',
  'agendando visita en la misma conversación inicial.',
  'capturando datos claves y usándolos para futuras acciones.',
  'con un 100% de lectura de tus campañas.',
];

const HeaderPage = () => {
  const [currentText, setCurrentText] = useState(TypeWriterTexts[0]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentDisplayText, setCurrentDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const { isMobile } = useIsMobile();

  const buttonHref =
    typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'http://localhost:3000/demochat/example-chat/newchat'
      : 'https://zeller.ai/demochat/example-chat/newchat';

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isDeleting && currentIndex < currentText.length) {
        setCurrentDisplayText(currentText.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      } else if (!isDeleting && currentIndex === currentText.length) {
        setTimeout(() => setIsDeleting(true), 1000);
      } else if (isDeleting && currentIndex > 0) {
        setCurrentDisplayText(currentText.slice(0, currentIndex - 1));
        setCurrentIndex(currentIndex - 1);
      } else if (isDeleting && currentIndex === 0) {
        const currentTextIndex = TypeWriterTexts.indexOf(currentText);
        setCurrentText(TypeWriterTexts[(currentTextIndex + 1) % TypeWriterTexts.length]);
        setIsDeleting(false);
      }
    }, 30);

    return () => clearInterval(interval);
  }, [currentText, currentIndex, isDeleting]);
  return (
    <Box sx={{ width: '100%' }}>
      <GradientBackground>
        <Box
          display="flex"
          mx="auto"
          justifyContent="center"
          alignItems="center"
          // px={3}
          py={4}
          flexDirection={{ lg: 'row', xs: 'column' }}
        >
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              width: { lg: '50%', xs: '100%' },
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'inline',
                maxWidth: { lg: '80%', xs: '90%' },
                margin: { lg: '0 auto', xs: '0 20px' },
              }}
            >
              <Typography
                sx={{
                  mt: { lg: 0, xs: 8 },
                  mb: 3,
                  fontSize: { lg: 62, xs: 50 },
                  lineHeight: { lg: '60px', xs: '47px' },
                  fontWeight: 'medium',
                  color: 'white',
                  textAlign: { lg: 'left', xs: 'left' },
                }}
              >
                Transforma tus ventas automotrices con el mejor vendedor de autos del mercado.
              </Typography>
              <Typography
                sx={{
                  fontSize: { lg: 32, xs: 20 },
                  fontFamily: 'Jost',
                  fontWeight: 'regular',
                  color: 'white',
                  lineHeight: '25px',
                  paddingRight: { lg: '120px', xs: '0px' },
                  textAlign: { lg: 'left', xs: 'left' },
                }}
              >
                Conduzcamos a una mejor conversión de Leads.
              </Typography>

              <Box mt={4} display="flex" justifyContent={{ xs: 'center', lg: 'flex-start' }}>
                <PrimaryBtn
                  variant="contained"
                  color="primary"
                  onClick={() => window.open(buttonHref, '_blank', 'noopener,noreferrer')}
                  startIcon={<WhatsAppIcon />}
                  sx={{
                    backgroundColor: '#25D366',
                    color: 'white',
                    padding: '12px 24px',
                    fontSize: isMobile ? '0.8rem' : '1.2rem',
                    '&:hover': {
                      backgroundColor: '#128C7E',
                      border: 'none',
                    },
                  }}
                >
                  Habla con nuestro vendedor de autos
                </PrimaryBtn>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              pb: { xs: 8, lg: 12 },
              justifyContent: 'center',
              display: 'flex',
              ml: { xs: 0, lg: -50 },
              mt: { xs: 4, lg: 0 },
              position: 'relative',
              width: '100%',
              ...(isMobile && {
                pr: 0,
                mr: 0,
                width: '100%',
              }),
            }}
          >
            <Box
              component="img"
              alt="Dashboard Zeller"
              src="/landing/zeller-landing-image.png"
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', lg: '100%' },
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
                margin: { xs: '0', lg: '0 auto' },
              }}
            />
          </Box>
        </Box>
      </GradientBackground>
      {!isMobile && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            mt: -10,
            mb: 0,
            backgroundColor: '#15183A',
            py: 3,
            borderBottomLeftRadius: 16,
            borderBottomRightRadius: 16,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              minWidth: '50%',
              maxWidth: '90%',
            }}
          >
            <Typography
              sx={{
                fontSize: 28,
                fontWeight: 500,
                color: 'white',
                mr: 1,
              }}
            >
              Eficienta tu proceso de ventas,
            </Typography>
            <Typography
              sx={{
                fontSize: 28,
                fontWeight: 500,
                color: '#F9D00A',
              }}
            >
              {currentDisplayText}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default HeaderPage;
